/**
 * CampaignHierarchyView — the read-only face of CampaignHierarchy, for the Event Detail page.
 *
 * Same tree, same look: it reuses the editor's nested <ul>/<li> and connector lines
 * (CampaignHierarchy.css), so the hierarchy reads the same on both pages. What differs is
 * that there is nothing to type into — each row is an icon, the campaign id in monospace, and
 * a badge where the node is another Event Hub event rather than a bare SFDC campaign.
 *
 * Fully expanded, no toggles: on a detail page the point is to see the whole tree at once,
 * and real hierarchies are a handful of nodes. Hand-rendered for the reason the editor is —
 * Cloudscape's TreeView is not exported at this version.
 */

import Badge from '@amzn/awsui-components-react/polaris/badge';
import Box from '@amzn/awsui-components-react/polaris/box';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import { countNodes, rootLabel, type HierarchyNode } from './campaignHierarchy';
import './CampaignHierarchy.css';

export interface CampaignHierarchyViewProps {
  eventName?: string;
  /** The event's own SFDC Campaign ID — the root's label. */
  campaignId?: string;
  nodes: readonly HierarchyNode[];
}

function Row({ node }: { node: HierarchyNode }) {
  return (
    <li className="eh-hierarchy__item">
      <div className="eh-hierarchy__row">
        <Icon name={node.children.length ? 'folder' : 'file'} variant="subtle" />
        <Box variant="code">{node.sfdcCampaignId || '—'}</Box>
        {node.eventId ? <Badge color="blue">Event</Badge> : null}
      </div>
      {node.children.length ? (
        <ul className="eh-hierarchy__children">
          {node.children.map((c) => (
            <Row key={c.key} node={c} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function CampaignHierarchyView({
  eventName,
  campaignId,
  nodes,
}: CampaignHierarchyViewProps) {
  const total = countNodes(nodes);
  return (
    <ul className="eh-hierarchy" aria-label="Event hierarchy">
      <li className="eh-hierarchy__item">
        <div className="eh-hierarchy__row eh-hierarchy__row--root">
          <Icon name="calendar" />
          <Box fontWeight="bold">{rootLabel(campaignId, eventName)}</Box>
          <Box color="text-body-secondary" fontSize="body-s">
            {total} {total === 1 ? 'child campaign' : 'child campaigns'}
          </Box>
        </div>
        <ul className="eh-hierarchy__children">
          {nodes.map((n) => (
            <Row key={n.key} node={n} />
          ))}
        </ul>
      </li>
    </ul>
  );
}
